import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { format, parseISO } from "date-fns";
import { MessageSquare, Lightbulb, EyeOff, Send, Megaphone, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusChip } from "@/components/incidents/StatusChip";

const fmt = d => { try { return format(parseISO(d), "d MMM yyyy"); } catch { return d; } };

export default function AskCommittee() {
  const { user } = useAuth();
  const canAsk = user?.role === "admin" || user?.role === "coach";
  const [meta, setMeta] = useState(null);
  const [form, setForm] = useState({ kind: "question", topic: "", message: "", anonymous: false });
  const [mine, setMine] = useState([]);
  const [published, setPublished] = useState([]);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("ask");
  const [params, setParams] = useSearchParams();
  const [addressee, setAddressee] = useState(null);

  useEffect(() => {
    const to = params.get("to");
    if (!to || !canAsk) return;
    api.get("/committee-contacts").then(r => {
      const c = r.data.find(x => x.id === to);
      if (c) { setAddressee(c); setTab("ask"); setForm(f => ({ ...f, message: f.message || `For the attention of the ${c.position} (${c.name}):\n\n` })); }
    }).catch(() => {});
  }, [params, canAsk]);
  const clearAddressee = () => { setAddressee(null); setParams({}, { replace: true }); };

  const load = () => {
    api.get("/committee/meta").then(r => setMeta(r.data)).catch(() => {});
    api.get("/committee/mine").then(r => setMine(r.data)).catch(() => {});
    api.get("/committee/published").then(r => setPublished(r.data)).catch(() => {});
  };
  useEffect(() => { load(); if (!canAsk) setTab("published"); }, [canAsk]);

  const submit = async () => {
    if (!form.topic || form.message.trim().length < 5) { toast.error("Pick a topic and write at least a sentence"); return; }
    setBusy(true);
    try {
      await api.post("/committee/ask", { ...form, addressed_to: addressee ? `${addressee.position} — ${addressee.name}` : null });
      toast.success(form.anonymous ? "Sent anonymously — the committee has been notified" : "Sent — the committee has been notified");
      setForm({ kind: "question", topic: "", message: "", anonymous: false }); clearAddressee(); load(); setTab("mine");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Couldn't send"); }
    finally { setBusy(false); }
  };

  const Card = ({ q }) => (
    <div className="card-tactical rounded-md p-4 space-y-2" data-testid={`committee-item-${q.id}`}>
      <div className="flex flex-wrap items-center gap-2 text-xs opacity-70">
        {q.kind === "question" ? <MessageSquare className="w-3.5 h-3.5 text-[#83B68B]" /> : <Lightbulb className="w-3.5 h-3.5 text-amber-300" />}
        <span className="font-display font-bold">{q.ref}</span><span className="uppercase tracking-widest">{q.kind} · {q.topic}</span>
        {q.addressed_to && <span className="px-2 py-0.5 rounded border border-[#83B68B]/50 text-[#83B68B] text-[9px] tracking-widest uppercase">For: {q.addressed_to}</span>}
        <StatusChip status={q.status === "on_agenda" ? "under_review" : q.status === "answered" ? "actioned" : q.status === "discussed" ? "awaiting_info" : q.status} labels={{ under_review: "On agenda", actioned: "Answered", awaiting_info: "Discussed", new: "New", submitted: "New", closed: "Closed" }} />
        <span className="ml-auto">{fmt(q.created_at)} · {q.author_name || <span className="inline-flex items-center gap-1"><EyeOff className="w-3 h-3" /> Anonymous</span>}</span>
      </div>
      <div className="text-sm whitespace-pre-wrap">{q.message}</div>
      {q.meeting_date && <div className="text-xs opacity-60">Committee meeting: {fmt(q.meeting_date)}</div>}
      {q.reveal && q.is_mine && <div className="text-xs text-amber-300">Your identity was revealed to the committee by {q.reveal.by} (Welfare Officer): "{q.reveal.reason}"</div>}
      {q.response && (
        <div className="rounded border border-[#83B68B]/40 bg-[#83B68B]/5 p-3 text-sm" data-testid={`committee-response-${q.id}`}>
          <div className="text-[10px] tracking-widest uppercase text-[#83B68B] mb-1 inline-flex items-center gap-1"><Megaphone className="w-3 h-3" /> Committee response · {q.response.by} · {fmt(q.response.at)} · {q.response.visibility === "published" ? "published to the club" : "private to you"}</div>
          <div className="whitespace-pre-wrap">{q.response.text}</div>
        </div>
      )}
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto px-4 py-8 space-y-6" data-testid="committee-page">
      <div>
        <div className="text-[10px] tracking-[0.3em] uppercase text-[#83B68B]">Club committee</div>
        <h1 className="font-display font-black text-4xl sm:text-5xl mt-1">Ask the Committee</h1>
        <p className="opacity-60 text-sm mt-1">Questions and feedback go straight to the committee, get tabled at the next meeting, and you're told the outcome. You can send anonymously.</p>
      </div>
      <div className="flex gap-2 flex-wrap" data-testid="committee-tabs">
        {[canAsk && ["ask", "Ask / give feedback"], canAsk && ["mine", `My submissions (${mine.length})`], ["published", `Committee answers (${published.length})`]].filter(Boolean).map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`committee-tab-${k}`} className={`px-4 py-2 rounded text-[10px] tracking-widest uppercase border ${tab === k ? "border-[#83B68B] bg-[#83B68B]/15 text-[#83B68B]" : "border-border hover:border-white/40"}`}>{l}</button>
        ))}
      </div>

      {tab === "ask" && canAsk && (
        <div className="card-tactical rounded-md p-5 space-y-4" data-testid="committee-form">
          {addressee && (
            <div className="flex items-center gap-3 rounded border border-[#83B68B]/50 bg-[#83B68B]/10 px-3 py-2 text-sm" data-testid="committee-addressee">
              <span className="text-[10px] tracking-widest uppercase text-[#83B68B]">For the attention of</span>
              <span className="font-display font-bold">{addressee.name}</span><span className="opacity-60">· {addressee.position}</span>
              <button type="button" onClick={clearAddressee} data-testid="committee-addressee-clear" className="ml-auto p-1 rounded opacity-60 hover:opacity-100" aria-label="Remove addressee"><X className="w-3.5 h-3.5" /></button>
            </div>
          )}
          <div className="grid grid-cols-2 gap-2">
            {[["question", "Question", MessageSquare], ["feedback", "Feedback", Lightbulb]].map(([v, l, I]) => (
              <button key={v} type="button" onClick={() => setForm({ ...form, kind: v })} data-testid={`committee-kind-${v}`} className={`p-3 rounded-md border text-left font-display font-bold inline-flex items-center gap-2 ${form.kind === v ? "border-[#83B68B] bg-[#83B68B]/10" : "border-border"}`}><I className="w-4 h-4 text-[#83B68B]" /> {l}</button>
            ))}
          </div>
          <div>
            <Label className="text-[10px] tracking-widest uppercase opacity-70">Topic</Label>
            <Select value={form.topic} onValueChange={v => setForm({ ...form, topic: v })}>
              <SelectTrigger data-testid="committee-topic" className="mt-1.5"><SelectValue placeholder="Choose a topic" /></SelectTrigger>
              <SelectContent>{(meta?.topics || []).map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] tracking-widest uppercase opacity-70">Your {form.kind}</Label>
            <Textarea rows={5} value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} data-testid="committee-message" className="mt-1.5" placeholder={form.kind === "question" ? "What would you like the committee to answer?" : "What's working, what isn't, and what would you change?"} />
          </div>
          <label className="flex items-start gap-3 cursor-pointer" data-testid="committee-anonymous">
            <Switch checked={form.anonymous} onCheckedChange={v => setForm({ ...form, anonymous: v })} data-testid="committee-anonymous-switch" />
            <div className="text-xs"><div className="font-semibold inline-flex items-center gap-1"><EyeOff className="w-3 h-3" /> Send anonymously</div><div className="opacity-60">The committee won't see your name. Only the Club Welfare Officer can unseal it — with a written reason, recorded in the audit log, and you'd be told.</div></div>
          </label>
          <button type="button" onClick={submit} disabled={busy} data-testid="committee-submit" className="btn-volt px-5 py-3 rounded-md inline-flex items-center gap-2 disabled:opacity-40"><Send className="w-4 h-4" /> {busy ? "Sending…" : "Send to the committee"}</button>
        </div>
      )}
      {tab === "mine" && (mine.length ? <div className="space-y-3" data-testid="committee-mine">{mine.map(q => <Card key={q.id} q={q} />)}</div> : <div className="card-tactical rounded-md p-10 text-center opacity-60">You haven't sent anything yet.</div>)}
      {tab === "published" && (published.length ? <div className="space-y-3" data-testid="committee-published">{published.map(q => <Card key={q.id} q={q} />)}</div> : <div className="card-tactical rounded-md p-10 text-center opacity-60">No published answers yet.</div>)}
    </div>
  );
}
